import React, { useState, useEffect, useMemo } from 'react';
import { useAppData } from '../contexts/AppDataContext';
import { useLocale } from '../contexts/LocaleContext';
import { watchlistService } from '../services/watchlistService';
import {
  Plus,
  RefreshCw,
  Factory,
  Package,
  Truck,
  Wrench,
  Building2,
  Map as MapIcon,
  LayoutList,
} from 'lucide-react';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import AddToWatchlistModal from '../components/Watchlist/AddToWatchlistModal';
import WatchlistSupplyChainMap from '../components/Watchlist/WatchlistSupplyChainMap';
import WatchlistListLayout from '../components/Watchlist/WatchlistListLayout';
import RouteDetailsDialog from '../components/Watchlist/RouteDetailsDialog';
import { useWatchlistPageMapData } from '../hooks/useWatchlistPageMapData';
import { colors } from '../config/colors';
import { spacing } from '../config/spacing';
import { Card } from '../components/UI/card';
import { Badge } from '../components/UI/badge';
import { Button } from '../components/UI/button';
import { COMPANY_TYPES } from '../models/watchlistModels';
import toast from 'react-hot-toast';
import {
  collectEmbeddedRouteAnalyses,
  routesNeedingAnalysis,
} from '../utils/watchlistReadModelAdapter';

const PAGE_SIZE = 5;

const Watchlist = () => {
  const { t } = useLocale();
  const {
    watchlist,
    companies,
    watchlistLoading,
    refreshWatchlist,
    tenantNodesByKey,
    tenantSuppliers,
    tenantCompanyRoutes,
    tenantProfile,
    tenantLoading,
    tenantMapReady,
    refreshTenantCompany,
  } = useAppData();
  const [viewMode, setViewMode] = useState('map');
  const [routeAnalyses, setRouteAnalyses] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [showRouteDetailsForRouteId, setShowRouteDetailsForRouteId] = useState(null);
  const [analyzingRoutes, setAnalyzingRoutes] = useState({});
  const [kanbanDraggedCompanyId, setKanbanDraggedCompanyId] = useState(null);
  const [kanbanDropTargetId, setKanbanDropTargetId] = useState(null);
  const [kanbanUpdating, setKanbanUpdating] = useState(false);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);

  const allRoutesWithItem = useMemo(
    () =>
      (watchlist || []).flatMap((item) =>
        (item.userCompanyInfo?.routes ?? []).map((route) => ({ item, route }))
      ),
    [watchlist]
  );
  const routeAnalysisInput = useMemo(
    () =>
      allRoutesWithItem.map(({ item, route }) => ({
        routeId: route.routeId ?? route.route_id,
        _ownerCompanyId: route._ownerCompanyId ?? item.companyId ?? item.company_id,
      })),
    [allRoutesWithItem]
  );
  const routeAnalysisKey = useMemo(
    () =>
      routeAnalysisInput.map((entry) => `${entry._ownerCompanyId}|${entry.routeId}`).sort().join(','),
    [routeAnalysisInput]
  );
  const routeToCompany = useMemo(() => {
    const m = {};
    (watchlist || []).forEach((i) =>
      (i.userCompanyInfo?.routes || []).forEach((r) => {
        const rid = r.routeId ?? r.route_id;
        if (rid != null) m[rid] = r._ownerCompanyId ?? i.companyId ?? i.company_id;
      })
    );
    return m;
  }, [watchlist]);

  const allAddresses = useMemo(
    () => (watchlist || []).flatMap((i) => i.userCompanyInfo?.userAddresses || []),
    [watchlist]
  );
  const addressMap = useMemo(() => {
    const m = {};
    allAddresses.forEach((a) => {
      const id = a.addressId ?? a.address_id;
      if (id != null) {
        m[String(id)] = a;
      }
    });
    return m;
  }, [allAddresses]);

  useEffect(() => {
    if (!watchlist?.length || routeAnalysisInput.length === 0) {
      setRouteAnalyses({});
      return;
    }
    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
    if (!userId) return;

    const embedded = collectEmbeddedRouteAnalyses(watchlist);
    setRouteAnalyses(embedded);

    const missing = routesNeedingAnalysis(watchlist);
    if (missing.length === 0) return;

    let cancelled = false;
    watchlistService.getRouteAnalysesBatch(userId, missing).then((result) => {
      if (cancelled || !result.success || !Array.isArray(result.data)) return;
      setRouteAnalyses((prev) => {
        const analyses = { ...prev };
        result.data.forEach((analysis) => {
          const rid = analysis.routeId ?? analysis.route_id;
          if (rid != null) analyses[rid] = analysis;
        });
        return analyses;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [watchlist?.length, routeAnalysisInput, routeAnalysisKey, watchlist]);

  useEffect(() => {
    if (!tenantMapReady && !tenantLoading) {
      refreshTenantCompany();
    }
  }, [tenantMapReady, tenantLoading, refreshTenantCompany]);

  const cardStyle = useMemo(
    () => ({
      backgroundColor: colors.background.card ?? colors.background.secondary,
      borderColor: colors.border.light,
      boxShadow: spacing.shadow?.lg ?? '0 6px 18px rgba(32,41,50,0.04)',
      borderRadius: spacing.radius?.lg ?? 12,
    }),
    []
  );

  useEffect(() => {
    if (allRoutesWithItem.length > 0 && selectedRouteIndex >= allRoutesWithItem.length) {
      setSelectedRouteIndex(Math.max(0, allRoutesWithItem.length - 1));
    }
  }, [allRoutesWithItem.length, selectedRouteIndex]);

  const handleAnalyzeRoute = async (routeId) => {
    const companyId = routeToCompany[routeId];
    if (!companyId) return;
    try {
      setAnalyzingRoutes((prev) => ({ ...prev, [routeId]: true }));
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
      const result = await watchlistService.getRouteAnalysis(userId, companyId, routeId);
      if (result.success && result.data) {
        setRouteAnalyses((prev) => ({ ...prev, [routeId]: result.data }));
        toast.success(t('routeAnalysisUpdated'));
      } else {
        toast.error(result.message || t('routeAnalysisFailed'));
      }
    } catch (err) {
      toast.error(t('routeAnalysisFailed'));
    } finally {
      setAnalyzingRoutes((prev) => ({ ...prev, [routeId]: false }));
    }
  };

  const mapRiskAnalyses = useMemo(() => {
    const m = {};
    const applyRisk = (id, score) => {
      if (id == null || score == null || Number(score) <= 0) return;
      m[String(id)] = { overallRiskScore: Number(score) };
    };
    (companies || []).forEach((c) => {
      const score = c.overallRiskScore ?? c.latestRiskAnalysis?.overallRiskScore;
      applyRisk(c.ticker, score);
      applyRisk(c.id, score);
    });
    (watchlist || []).forEach((item) => {
      const cid = item.companyId ?? item.company_id;
      const score = item.company?.overallRiskScore ?? item.company?.overall_risk_score;
      if (score != null) applyRisk(cid, score);
    });
    return m;
  }, [companies, watchlist]);

  const { markers, routes, bounds, geocoding, hasMapContent } = useWatchlistPageMapData({
    watchlist,
    tenantNodesByKey,
    tenantSuppliers,
    tenantCompanyRoutes,
    tenantProfile,
    companies,
    mapRiskAnalyses,
    routeAnalyses,
    mapRefreshTrigger,
  });

  const companyById = useMemo(() => {
    const map = {};
    (companies || []).forEach((c) => {
      map[c.id] = c;
      if (c.ticker) map[c.ticker] = c;
    });
    return map;
  }, [companies]);

  const mapStats = useMemo(() => {
    const companyIds = new Set();
    (watchlist || []).forEach((item) => {
      const id = item.companyId ?? item.company_id;
      if (id != null) companyIds.add(String(id));
    });
    (tenantSuppliers || []).forEach((supplier) => {
      const id = supplier.ticker ?? supplier.id ?? supplier.companyId;
      if (id != null) companyIds.add(String(id));
    });
    markers.forEach((marker) => {
      if (marker.type === 'supplier' && marker.supplierTicker) {
        companyIds.add(String(marker.supplierTicker));
      }
    });
    const companyCount = companyIds.size;
    const facilityCount = markers.length;
    const routeCount = routes.length;
    const highRiskCount = markers.filter((marker) => {
      const score = Number(marker.riskScore);
      return (
        (Number.isFinite(score) && score >= 7) ||
        String(marker.priority || '').toLowerCase() === 'high'
      );
    }).length;
    return { companyCount, facilityCount, routeCount, highRiskCount };
  }, [watchlist, tenantSuppliers, markers, routes]);

  const getRiskLevel = (item) => {
    if (item.priority === 'high') return 'riskLevelHigh';
    if (item.priority === 'low') return 'riskLevelLow';
    return 'riskLevelModerate';
  };

  const handleAdd = async (request) => {
    const { companyType, ...addPayload } = request;
    const result = await watchlistService.addToCurrentUserWatchlist(addPayload);
    if (result.success) {
      if (companyType?.trim()) {
        const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
        if (userId) {
          await watchlistService.setCompanyType(userId, request.companyId, companyType.trim());
        }
      }
      toast.success(t('addedToWatchlist'));
      setShowAddModal(false);
      refreshWatchlist();
    } else {
      toast.error(result.message || t('failedToAddToWatchlist'));
    }
  };

  const KANBAN_COLUMNS = useMemo(
    () => [
      {
        id: 'raw_material',
        labelKey: 'kanbanRawMaterial',
        types: [COMPANY_TYPES.RAW_MATERIAL_SUPPLIER],
        icon: Factory,
        color: colors.info[500],
      },
      {
        id: 'component_equipment',
        labelKey: 'kanbanComponentEquipment',
        types: [COMPANY_TYPES.COMPONENT_MANUFACTURER, COMPANY_TYPES.EQUIPMENT_SUPPLIER],
        icon: Wrench,
        color: colors.primary[500],
      },
      {
        id: 'finished_packaging',
        labelKey: 'kanbanFinishedPackaging',
        types: [COMPANY_TYPES.FINISHED_GOODS_MANUFACTURER, COMPANY_TYPES.PACKAGING_COMPANY],
        icon: Package,
        color: colors.secondary[500],
      },
      {
        id: 'logistics_services',
        labelKey: 'kanbanLogisticsServices',
        types: [
          COMPANY_TYPES.LOGISTICS_COMPANY,
          COMPANY_TYPES.SERVICE_PROVIDER,
          COMPANY_TYPES.UTILITY_COMPANY,
          COMPANY_TYPES.CONTRACT_MANUFACTURER,
          COMPANY_TYPES.RESEARCH_DEVELOPMENT_COMPANY,
        ],
        icon: Truck,
        color: colors.success[500],
      },
      {
        id: 'unassigned',
        labelKey: 'kanbanUnassigned',
        types: ['unassigned'],
        icon: Building2,
        color: colors.gray[400],
      },
    ],
    []
  );

  const kanbanGroups = useMemo(() => {
    const groups = {};
    KANBAN_COLUMNS.forEach((col) => {
      groups[col.id] = [];
    });
    (watchlist || []).forEach((item) => {
      const info = item.userCompanyInfo;
      const rawType = (info?.companyType ?? info?.company_type ?? '').toString().trim().toLowerCase();
      const found = KANBAN_COLUMNS.find((col) =>
        col.types.some((ty) => String(ty).toLowerCase() === rawType || (ty === 'unassigned' && !rawType))
      );
      const key = found ? found.id : 'unassigned';
      if (groups[key]) groups[key].push(item);
    });
    return groups;
  }, [watchlist, KANBAN_COLUMNS]);

  const getCompanyTypeForColumn = (columnId) => {
    const col = KANBAN_COLUMNS.find((c) => c.id === columnId);
    if (!col || col.types[0] === 'unassigned') return '';
    return col.types[0];
  };

  const handleKanbanDragStart = (e, item) => {
    setKanbanDraggedCompanyId(item.companyId);
    e.dataTransfer.setData('application/json', JSON.stringify({ companyId: item.companyId }));
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
  };

  const handleKanbanDragEnd = () => {
    setKanbanDraggedCompanyId(null);
    setKanbanDropTargetId(null);
  };

  const handleKanbanDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setKanbanDropTargetId(columnId);
  };

  const handleKanbanDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setKanbanDropTargetId(null);
    }
  };

  const getColumnIdForItem = (item) => {
    const info = item.userCompanyInfo;
    const rawType = (info?.companyType ?? info?.company_type ?? '').toString().trim().toLowerCase();
    const found = KANBAN_COLUMNS.find((col) =>
      col.types.some((ty) => String(ty).toLowerCase() === rawType || (ty === 'unassigned' && !rawType))
    );
    return found ? found.id : 'unassigned';
  };

  const handleKanbanDrop = async (e, targetColumnId) => {
    e.preventDefault();
    setKanbanDropTargetId(null);
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const { companyId } = payload;
    if (!companyId) return;
    const item = (watchlist || []).find((w) => (w.companyId ?? w.company_id) === companyId);
    if (item && getColumnIdForItem(item) === targetColumnId) return;
    const targetType = getCompanyTypeForColumn(targetColumnId);
    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
    if (!userId) return;
    setKanbanUpdating(true);
    try {
      const result = await watchlistService.setCompanyType(userId, companyId, targetType);
      if (result.success) {
        toast.success(t('kanbanCompanyTypeUpdated'));
        refreshWatchlist();
      } else {
        toast.error(result.message || t('kanbanCompanyTypeUpdateFailed'));
      }
    } catch (err) {
      toast.error(t('kanbanCompanyTypeUpdateFailed'));
    } finally {
      setKanbanDraggedCompanyId(null);
      setKanbanUpdating(false);
    }
  };

  const handleViewRouteAnalysis = (route) => {
    const routeId = route?.routeId;
    if (routeId == null) return;
    const analysis = routeAnalyses[routeId];
    if (analysis) {
      setShowRouteDetailsForRouteId(routeId);
      return;
    }
    setShowRouteDetailsForRouteId(routeId);
  };

  const isMapBootstrapping =
    (watchlistLoading || tenantLoading) && !hasMapContent && !tenantMapReady;

  if (isMapBootstrapping && !(watchlist || []).length) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ gap: spacing.scale?.[6] ?? 24 }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border-light bg-background-secondary p-1">
          <Button
            type="button"
            variant={viewMode === 'map' ? 'default' : 'ghost'}
            size="sm"
            className="gap-2"
            onClick={() => setViewMode('map')}
            aria-pressed={viewMode === 'map'}
          >
            <MapIcon className="h-4 w-4" />
            {t('watchlistViewMap')}
          </Button>
          <Button
            type="button"
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            className="gap-2"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
          >
            <LayoutList className="h-4 w-4" />
            {t('watchlistViewList')}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              refreshWatchlist();
              refreshTenantCompany();
              setMapRefreshTrigger((prev) => prev + 1);
            }}
            aria-label={t('refreshData')}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button type="button" variant="default" size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('addCompany')}
          </Button>
        </div>
      </div>

      {viewMode === 'map' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { key: 'companies', value: mapStats.companyCount, labelKey: 'watchlistMapStatCompanies' },
              { key: 'facilities', value: mapStats.facilityCount, labelKey: 'watchlistMapStatFacilities' },
              { key: 'routes', value: mapStats.routeCount, labelKey: 'watchlistMapStatRoutes' },
              { key: 'highRisk', value: mapStats.highRiskCount, labelKey: 'watchlistMapStatHighRisk' },
            ].map((stat) => (
              <Card key={stat.key} className="border border-border-light bg-background-card px-4 py-3">
                <p className="text-xs text-text-secondary">{t(stat.labelKey)}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-xl font-semibold text-text-primary">{stat.value}</span>
                  {stat.key === 'highRisk' && stat.value > 0 ? (
                    <Badge variant="outline" className="border-danger-500/30 text-danger-500">
                      {t('riskLevelHigh')}
                    </Badge>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>

          <WatchlistSupplyChainMap
            markers={markers}
            routes={routes}
            bounds={bounds}
            loading={isMapBootstrapping && !hasMapContent}
            geocoding={geocoding}
            selectedMarkerId={selectedMarkerId}
            selectedRouteId={selectedRouteId}
            onMarkerSelect={setSelectedMarkerId}
            onRouteSelect={setSelectedRouteId}
            onViewRouteAnalysis={handleViewRouteAnalysis}
            onAddCompany={() => setShowAddModal(true)}
          />
        </div>
      ) : (
        <WatchlistListLayout
          watchlist={watchlist || []}
          companies={companies || []}
          cardStyle={cardStyle}
          pageSize={PAGE_SIZE}
          kanbanColumns={KANBAN_COLUMNS}
          kanbanGroups={kanbanGroups}
          kanbanDraggedCompanyId={kanbanDraggedCompanyId}
          kanbanDropTargetId={kanbanDropTargetId}
          kanbanUpdating={kanbanUpdating}
          onKanbanDragStart={handleKanbanDragStart}
          onKanbanDragEnd={handleKanbanDragEnd}
          onKanbanDragOver={handleKanbanDragOver}
          onKanbanDragLeave={handleKanbanDragLeave}
          onKanbanDrop={handleKanbanDrop}
          companyById={companyById}
          getRiskLevel={getRiskLevel}
          markers={markers}
          routes={routes}
          bounds={bounds}
          geocoding={geocoding}
          onMapRefresh={() => setMapRefreshTrigger((prev) => prev + 1)}
          allRoutesWithItem={allRoutesWithItem}
          selectedRouteIndex={selectedRouteIndex}
          onSelectedRouteIndexChange={setSelectedRouteIndex}
          addressMap={addressMap}
          showRouteDetailsForRouteId={showRouteDetailsForRouteId}
          onShowRouteDetailsForRouteId={setShowRouteDetailsForRouteId}
          routeAnalyses={routeAnalyses}
          analyzingRoutes={analyzingRoutes}
          onAnalyzeRoute={handleAnalyzeRoute}
          onAddCompany={() => setShowAddModal(true)}
        />
      )}

      {showRouteDetailsForRouteId != null && routeAnalyses[showRouteDetailsForRouteId] ? (
        <RouteDetailsDialog
          routeAnalysis={routeAnalyses[showRouteDetailsForRouteId]}
          onClose={() => setShowRouteDetailsForRouteId(null)}
          onAnalyze={() => handleAnalyzeRoute(showRouteDetailsForRouteId)}
          analyzing={!!analyzingRoutes[showRouteDetailsForRouteId]}
        />
      ) : null}

      {showAddModal ? (
        <AddToWatchlistModal
          companies={companies || []}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAdd}
        />
      ) : null}
    </div>
  );
};

export default Watchlist;
